import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { de } from 'date-fns/locale';
import { useBedarfeRecentEvents } from '../../api/bedarfe';
import { BedarfStatusBadge } from '../bedarfe/BedarfStatusBadge';
import type { BedarfRecentEvent, BedarfRecentEventType } from '../../types/bedarf';

const EVENT_CONFIG: Record<
  BedarfRecentEventType,
  { icon: string; label: string; bg: string; fg: string }
> = {
  BEDARF_CREATE: {
    icon: '+',
    label: 'Neu angelegt',
    bg: 'bg-green-100',
    fg: 'text-green-700',
  },
  BEDARF_STATUS_CHANGE: {
    icon: '→',
    label: 'Status geändert',
    bg: 'bg-blue-100',
    fg: 'text-blue-700',
  },
  BEDARF_DELETE: {
    icon: '×',
    label: 'Gelöscht',
    bg: 'bg-red-100',
    fg: 'text-red-700',
  },
};

function formatChildName(event: BedarfRecentEvent): string {
  const { childFirstName, childLastName } = event.bedarf;
  return childLastName ? `${childFirstName} ${childLastName}` : childFirstName;
}

function EventRow({ event }: { event: BedarfRecentEvent }) {
  const cfg = EVENT_CONFIG[event.eventType];
  const name = formatChildName(event);
  const isDeleted = event.eventType === 'BEDARF_DELETE';
  const timeAgo = formatDistanceToNow(new Date(event.occurredAt), {
    addSuffix: true,
    locale: de,
  });

  const nameEl = isDeleted ? (
    <span className="line-through text-gray-500">{name}</span>
  ) : (
    <Link
      to={`/bedarfe/${event.bedarfId}`}
      className="font-medium text-gray-900 hover:text-primary-600"
    >
      {name}
    </Link>
  );

  return (
    <li className="flex gap-3 py-3">
      <div
        className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full font-bold ${cfg.bg} ${cfg.fg}`}
        aria-label={cfg.label}
      >
        {cfg.icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {nameEl}
            <span className="text-xs text-gray-500 shrink-0">{cfg.label}</span>
          </div>
          <span className="text-xs text-gray-400 shrink-0" title={new Date(event.occurredAt).toLocaleString('de-DE')}>
            {timeAgo}
          </span>
        </div>
        {event.bedarf.schoolCityName && (
          <div className="text-xs text-gray-600 mt-0.5">
            Schulstadt: <span className="font-medium">{event.bedarf.schoolCityName}</span>
          </div>
        )}
        {event.statusChange && (
          <div className="flex items-center gap-2 mt-1">
            <BedarfStatusBadge status={event.statusChange.from} size="sm" />
            <span className="text-gray-400 text-xs">→</span>
            <BedarfStatusBadge status={event.statusChange.to} size="sm" />
          </div>
        )}
      </div>
    </li>
  );
}

export function BedarfLiveFeed() {
  const { data, isLoading, error } = useBedarfeRecentEvents(7);
  const events = data?.data ?? [];

  return (
    <div className="bg-white rounded-lg shadow flex flex-col max-h-[70vh]">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">
          Live-Feed Bedarfe (7 Tage)
        </h2>
        {!isLoading && !error && (
          <span className="text-xs text-gray-500">{events.length}</span>
        )}
      </div>
      <div className="flex-1 overflow-y-auto px-6">
        {isLoading ? (
          <div className="py-8 text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-600">
            Fehler beim Laden
          </p>
        ) : events.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            Keine Aktivitäten in den letzten 7 Tagen
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {events.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
